<template>
  <div class="p-4">
    <!-- Header -->
    <div class="mb-4 flex justify-between items-center">
      <div>
        <h1 class="text-xl font-bold text-gray-900 dark:text-white">Cotizaciones</h1>
        <p class="text-sm text-gray-600 dark:text-gray-400">Gestiona cotizaciones y propuestas para clientes</p>
      </div>
      <button 
        @click="$router.push('/cotizaciones/nueva')" 
        class="px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors flex items-center gap-2"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
        </svg>
        Nueva Cotización
      </button>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <div class="flex flex-wrap gap-4">
        <div class="flex-1 min-w-64">
          <input 
            v-model="filters.search" 
            @input="debouncedSearch"
            type="text" 
            placeholder="Buscar por número o cliente..." 
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
          />
        </div>
        <div>
          <select 
            v-model="filters.estado" 
            @change="fetchCotizaciones(1)"
            class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 dark:bg-gray-700 dark:text-white text-sm"
          >
            <option value="">Todos los estados</option>
            <option value="borrador">Borradores</option>
            <option value="enviada">Enviadas</option>
            <option value="aprobada">Aprobadas</option>
            <option value="rechazada">Rechazadas</option>
            <option value="vencida">Vencidas</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Tabla de cotizaciones -->
    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="p-4">
        <div v-if="error" class="text-red-500 text-center mb-3 text-sm">{{ error }}</div>
        <div class="overflow-x-auto">
          <table class="w-full text-xs text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs uppercase bg-gray-100 dark:bg-gray-800 dark:text-gray-300 border-b-2 border-gray-200 dark:border-gray-700">
              <tr>
                <th class="px-3 py-2 font-bold">Número</th>
                <th class="px-3 py-2 font-bold">Nombre</th>
                <th class="px-3 py-2 font-bold">Cliente</th>
                <th class="px-3 py-2 font-bold">Estado</th>
                <th class="px-3 py-2 font-bold">Items</th>
                <th class="px-3 py-2 font-bold">Total</th>
                <th class="px-3 py-2 font-bold">Fecha</th>
                <th class="px-3 py-2 font-bold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cotizacion in cotizaciones" :key="cotizacion.id" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 even:bg-gray-50 dark:even:bg-gray-700">
                <td class="px-3 py-2 font-medium text-gray-900 dark:text-white">{{ cotizacion.numero_cotizacion }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">{{ cotizacion.nombre }}</td>
                <td class="px-3 py-2 text-gray-700 dark:text-gray-200">{{ cotizacion.cliente_nombre || 'Sin asignar' }}</td>
                <td class="px-3 py-2">
                  <span :class="getEstadoBadgeClass(cotizacion.estado)" class="px-2 py-0.5 rounded-full text-xs font-medium">
                    {{ getEstadoText(cotizacion.estado) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full dark:bg-blue-900 dark:text-blue-300">
                    {{ cotizacion.items_count || 0 }}
                  </span>
                </td>
                <td class="px-3 py-2 text-center">
                  <span class="text-green-600 dark:text-green-400 font-medium">
                    ${{ parseFloat(cotizacion.total || 0).toFixed(2) }}
                  </span>
                </td>
                <td class="px-3 py-2 text-gray-500 dark:text-gray-400">
                  {{ formatDate(cotizacion.created_at) }}
                </td>
                <td class="px-3 py-2 whitespace-nowrap text-right">
                  <button @click="viewCotizacion(cotizacion)" class="text-blue-500 hover:text-blue-700 mr-1" title="Ver detalles">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </button>
                  <button @click="editCotizacion(cotizacion)" class="text-green-500 hover:text-green-700 mr-1" title="Editar">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                    </svg>
                  </button>
                  <button
                    v-if="cotizacion.estado === 'aprobada'"
                    @click="convertirAPedido(cotizacion)"
                    class="text-violet-500 hover:text-violet-700 mr-1"
                    title="Convertir a Pedido"
                  >
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"/>
                    </svg>
                  </button>
                  <button @click="downloadPDF(cotizacion)" class="text-orange-500 hover:text-orange-700 mr-1" title="Descargar PDF">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                    </svg>
                  </button>
                  <button @click="duplicateCotizacion(cotizacion)" class="text-purple-500 hover:text-purple-700 mr-1" title="Duplicar">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                    </svg>
                  </button>
                  <button @click="onDeleteCotizacion(cotizacion)" class="text-red-500 hover:text-red-700" title="Eliminar">
                    <svg class="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                    </svg>
                  </button>
                </td>
              </tr>
              <tr v-if="cotizaciones.length === 0">
                <td colspan="8" class="text-center py-6 text-gray-400 text-sm">
                  {{ loading ? 'Cargando...' : 'No hay cotizaciones para mostrar.' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Paginación -->
      <Pagination
        v-if="pagination && pagination.total > 0"
        :current-page="pagination.current_page"
        :total-pages="pagination.last_page"
        :total="pagination.total"
        :per-page="pagination.per_page"
        @page-changed="fetchCotizaciones"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useHttp } from '@/composables/useHttp'
import { useNotifications } from '@/composables/useNotifications'
import Pagination from '@/components/Pagination.vue'
import Swal from 'sweetalert2'

const router = useRouter()
const { get, del, post, loading, error, data } = useHttp()
const { showSuccess, showError } = useNotifications()

// Estado
const cotizaciones = ref([])
const pagination = ref(null)
const filters = ref({
  search: '',
  estado: ''
})

// Cargar cotizaciones
const fetchCotizaciones = async (page = 1) => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: '15'
    })

    if (filters.value.search) {
      params.append('numero_cotizacion', filters.value.search)
      params.append('cliente_nombre', filters.value.search)
    }

    if (filters.value.estado) {
      params.append('estado', filters.value.estado)
    }

    await get(`cotizaciones?${params.toString()}`)
    if (data.value) {
      cotizaciones.value = data.value.data || data.value
      pagination.value = data.value.meta || data.value
    }
  } catch (err) {
    showError('Error al cargar cotizaciones')
  }
}

// Búsqueda con debounce
const debouncedSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      fetchCotizaciones(1)
    }, 500)
  }
})()

// Acciones
const viewCotizacion = (cotizacion) => {
  router.push(`/cotizaciones/${cotizacion.id}`)
}

const editCotizacion = (cotizacion) => {
  router.push(`/cotizaciones/${cotizacion.id}/editar`)
}

const downloadPDF = async (cotizacion) => {
  try {
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'
    const token = localStorage.getItem('token')

    const response = await fetch(`${API_BASE_URL}/cotizaciones/${cotizacion.id}/pdf`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      throw new Error('Error al descargar el PDF')
    }

    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `cotizacion-${cotizacion.numero_cotizacion}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)

    showSuccess('PDF descargado correctamente')
  } catch (error) {
    console.error('Error al descargar PDF:', error)
    showError('Error al descargar el PDF')
  }
}

const duplicateCotizacion = (cotizacion) => {
  // Implementar duplicación
  showSuccess('Función de duplicar próximamente')
}

const convertirAPedido = async (cotizacion) => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Convertir a Pedido?',
    html: `
      <p>¿Deseas convertir la cotización <strong>${cotizacion.numero_cotizacion}</strong> a un pedido?</p>
      <p class="text-sm text-gray-600 mt-2">Esto creará un nuevo pedido con todos los productos de la cotización.</p>
    `,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#8b5cf6',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, convertir',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await post('pedidos/from-cotizacion', { cotizacion_id: cotizacion.id })

      if (data.value) {
        showSuccess('Pedido creado exitosamente')

        // Preguntar si desea ver el pedido creado
        const { isConfirmed: verPedido } = await Swal.fire({
          title: 'Pedido Creado',
          text: `Se creó el pedido ${data.value.pedido?.numero_pedido || ''}. ¿Deseas verlo ahora?`,
          icon: 'success',
          showCancelButton: true,
          confirmButtonColor: '#8b5cf6',
          cancelButtonColor: '#6b7280',
          confirmButtonText: 'Ver pedido',
          cancelButtonText: 'Quedarme aquí'
        })

        if (verPedido && data.value.pedido) {
          router.push(`/pedidos/${data.value.pedido.id}`)
        } else {
          await fetchCotizaciones()
        }
      }
    } catch (err) {
      showError('No se pudo convertir la cotización a pedido')
    }
  }
}

const onDeleteCotizacion = async (cotizacion) => {
  const { isConfirmed } = await Swal.fire({
    title: '¿Eliminar cotización?',
    text: `¿Estás seguro de que deseas eliminar "${cotizacion.numero_cotizacion}"? Esta acción no se puede deshacer.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc2626',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  })

  if (isConfirmed) {
    try {
      await del(`cotizaciones/${cotizacion.id}`)
      await fetchCotizaciones()
      showSuccess('Cotización eliminada exitosamente')
    } catch (err) {
      showError('No se pudo eliminar la cotización')
    }
  }
}

// Utilidades
const getEstadoBadgeClass = (estado) => {
  const classes = {
    'borrador': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    'enviada': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'aprobada': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'rechazada': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    'vencida': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
  }
  return classes[estado] || classes.borrador
}

const getEstadoText = (estado) => {
  const texts = {
    'borrador': 'Borrador',
    'enviada': 'Enviada',
    'aprobada': 'Aprobada',
    'rechazada': 'Rechazada',
    'vencida': 'Vencida'
  }
  return texts[estado] || 'Borrador'
}

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

onMounted(() => {
  fetchCotizaciones()
})
</script>